import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  testConnection,
  enqueueJob,
  retryJob,
  cancelJob,
  createWebhook,
  deleteWebhook,
  upsertServiceMapping,
  deleteServiceMapping,
  updateCredentials,
  getConnection,
  listCredentialKeys,
  listMappings,
  listWebhooks,
  listJobs,
  listIntegrationEvents,
} from "@/lib/integrations.functions";
import {
  listCertifications,
  runCarrierCertification,
} from "@/lib/carrier-certification.functions";
import { runCertificationScenarios } from "@/lib/carrier-certification-runner.functions";
import { toast } from "sonner";
import { PROVIDER_FIELDS } from "@/lib/integrations/categories";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { listCartroverMerchants } from "@/lib/cartrover/cartrover.functions";
import { CartroverMerchantRulesDialog } from "@/components/admin/cartrover-merchant-rules-dialog";

export const Route = createFileRoute("/_authenticated/app/admin/integrations/$connectionId")({
  component: ConnectionDetailPage,
});

const JOB_KINDS = [
  "sync_orders",
  "sync_inventory",
  "push_tracking",
  "push_order_status",
  "generate_scan_form",
  "test_connection",
];

type Conn = { id: string; provider: string; label: string; env: string; status: string; tenant_id: string; customer_id: string | null; health_message: string | null; last_health_at: string | null };

function ConnectionDetailPage() {
  const { connectionId } = Route.useParams();
  const test = useServerFn(testConnection);
  const enqueue = useServerFn(enqueueJob);
  const get = useServerFn(getConnection);
  const { data, refetch } = useQuery({
    queryKey: ["integration_connection", connectionId],
    queryFn: () => get({ data: { connectionId } }),
  });
  const conn = (data?.connection ?? null) as Conn | null;
  const load = () => refetch();
  if (!conn) return <div className="p-4 sm:p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/app/admin/integrations" className="text-xs text-muted-foreground hover:underline">← Integrations</Link>
          <h2 className="text-xl font-semibold">{conn.label} <span className="text-xs text-muted-foreground uppercase ml-2">{conn.provider} · {conn.env}</span></h2>
        </div>
        <div className="flex gap-2 items-center">
          <Badge variant={conn.status === "active" ? "default" : conn.status === "error" ? "destructive" : "secondary"}>{conn.status}</Badge>
          {conn.provider === "cartrover" && (
            <Link
              to="/app/admin/integrations/cartrover/$connectionId"
              params={{ connectionId }}
              className="text-xs underline text-primary"
            >
              CartRover console →
            </Link>
          )}
          <Button size="sm" variant="outline" onClick={async () => { const r = await test({ data: { connectionId } }); toast[r.ok ? "success" : "error"](r.message ?? (r.ok ? "OK" : "Failed")); load(); }}>Test</Button>
        </div>
      </div>

      <MissingCredsBanner connectionId={connectionId} provider={conn.provider} />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="credentials">Credentials</TabsTrigger>
          <TabsTrigger value="mappings">Service mapping</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="jobs">Jobs</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="certification">Certification</TabsTrigger>
          {conn.provider === "cartrover" && (
            <TabsTrigger value="rules">Rules</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview">
          <Card><CardContent className="p-4 space-y-2 text-sm">
            <div><span className="text-muted-foreground">Provider:</span> {conn.provider}</div>
            <div><span className="text-muted-foreground">Env:</span> {conn.env}</div>
            <div><span className="text-muted-foreground">Status:</span> {conn.status}</div>
            {conn.health_message && <div><span className="text-muted-foreground">Last message:</span> {conn.health_message}</div>}
            <div className="pt-3 flex flex-wrap gap-2">
              {JOB_KINDS.map((k) => (
                <Button key={k} size="sm" variant="outline" onClick={async () => { try { await enqueue({ data: { connectionId, kind: k, payload: {} } }); toast.success(`${k} queued`); } catch (e) { toast.error((e as Error).message); } }}>{k}</Button>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="credentials"><CredentialsPanel connectionId={connectionId} provider={conn.provider} /></TabsContent>
        <TabsContent value="mappings"><MappingsPanel connectionId={connectionId} tenantId={conn.tenant_id} /></TabsContent>
        <TabsContent value="webhooks"><WebhooksPanel connectionId={connectionId} provider={conn.provider} /></TabsContent>
        <TabsContent value="jobs"><JobsPanel connectionId={connectionId} /></TabsContent>
        <TabsContent value="activity"><ActivityPanel connectionId={connectionId} /></TabsContent>
        <TabsContent value="certification">
          <CertificationPanel
            connectionId={connectionId}
            tenantId={conn.tenant_id}
            provider={conn.provider}
            env={conn.env}
          />
        </TabsContent>
        {conn.provider === "cartrover" && (
          <TabsContent value="rules">
            <CartroverRulesPanel connectionId={connectionId} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function CartroverRulesPanel({ connectionId }: { connectionId: string }) {
  const list = useServerFn(listCartroverMerchants);
  const { data } = useQuery({
    queryKey: ["cartrover_merchants_rules", connectionId],
    queryFn: () => list({ data: { connectionId } }),
  });
  const merchants = data?.merchants ?? [];
  const [openFor, setOpenFor] = useState<{ id: string; name: string } | null>(null);
  return (
    <Card>
      <CardContent className="p-4 space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Configure per-merchant rules: import behavior, edit permissions per order stage, inventory & product sync, tracking push, and source-of-truth ownership. Denied actions are hard-blocked and logged to exceptions.
        </p>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Merchant</TableHead>
                <TableHead>CR Merchant ID</TableHead>
                <TableHead>Sync</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {merchants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">
                    No merchants linked. Add one from the CartRover console.
                  </TableCell>
                </TableRow>
              )}
              {merchants.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">{m.display_name || m.merchant_name}</TableCell>
                  <TableCell className="font-mono text-xs">{m.cartrover_merchant_id}</TableCell>
                  <TableCell className="text-xs">
                    {[
                      m.order_import_enabled && "Orders",
                      m.inventory_sync_enabled && "Inv",
                      m.shipment_sync_enabled && "Ship",
                    ].filter(Boolean).join(" · ") || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenFor({ id: m.id, name: m.display_name || m.merchant_name })}
                    >
                      Configure rules
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <CartroverMerchantRulesDialog
          open={!!openFor}
          onOpenChange={(v) => !v && setOpenFor(null)}
          merchantId={openFor?.id ?? ""}
          merchantName={openFor?.name ?? ""}
        />
      </CardContent>
    </Card>
  );
}

function MissingCredsBanner({ connectionId, provider }: { connectionId: string; provider: string }) {
  const list = useServerFn(listCredentialKeys);
  const { data } = useQuery({
    queryKey: ["integration_credentials", connectionId],
    queryFn: () => list({ data: { connectionId } }),
  });
  const spec = PROVIDER_FIELDS[provider as keyof typeof PROVIDER_FIELDS];
  if (!spec) return null;
  const have = new Set((data?.keys ?? []).map((k) => k.key_name));
  const missing = spec.creds.filter((f) => !have.has(f.key));
  if (missing.length === 0) return null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 flex items-start gap-3">
      <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
      <div className="text-sm space-y-1">
        <div className="font-medium">Missing credentials</div>
        <div className="text-xs text-muted-foreground">
          This {spec.label} connection needs {missing.map((f) => f.label).join(", ")} before it can issue labels or run certification. Enter them in the <strong>Credentials</strong> tab below.
        </div>
      </div>
    </div>
  );
}

function CredentialsPanel({ connectionId, provider }: { connectionId: string; provider: string }) {
  const update = useServerFn(updateCredentials);
  const list = useServerFn(listCredentialKeys);
  const { data, refetch } = useQuery({
    queryKey: ["integration_credentials", connectionId],
    queryFn: () => list({ data: { connectionId } }),
  });
  const keys = data?.keys ?? [];
  const rotatedBy: Record<string, string | null> = Object.fromEntries(
    keys.map((k) => [k.key_name, k.rotated_at ?? null]),
  );
  const spec = PROVIDER_FIELDS[provider as keyof typeof PROVIDER_FIELDS];

  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function save() {
    const payload: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) if (v.trim()) payload[k] = v;
    if (Object.keys(payload).length === 0) {
      toast.error("Enter at least one value to save");
      return;
    }
    setBusy(true);
    try {
      await update({ data: { connectionId, credentials: payload } });
      toast.success("Credentials saved");
      setValues({});
      refetch();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-4 text-sm">
        <p className="text-muted-foreground text-xs">
          Values are encrypted at rest and never returned to the client. Leave a field blank to keep the existing value; fill it to rotate.
        </p>

        {spec ? (
          <div className="space-y-3">
            {spec.creds.map((f) => {
              const isSet = rotatedBy[f.key] != null;
              return (
                <div key={f.key} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`cred-${f.key}`}>{f.label}</Label>
                    {isSet ? (
                      <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                        Set · rotated {new Date(rotatedBy[f.key]!).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-xs text-destructive inline-flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Not set
                      </span>
                    )}
                  </div>
                  <Input
                    id={`cred-${f.key}`}
                    type={f.type ?? "text"}
                    autoComplete="off"
                    placeholder={isSet ? "•••••• (leave blank to keep)" : `Enter ${f.label}`}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                  {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
                </div>
              );
            })}
            <div className="flex justify-end pt-2 border-t">
              <Button size="sm" onClick={save} disabled={busy}>
                {busy ? "Saving…" : "Save credentials"}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">No credential schema defined for {provider}.</p>
        )}

        {/* Show any extra keys not in the provider spec (legacy / custom). */}
        {spec && keys.some((k) => !spec.creds.find((f) => f.key === k.key_name)) && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">Other stored keys</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Key</TableHead>
                    <TableHead>Last rotated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys
                    .filter((k) => !spec.creds.find((f) => f.key === k.key_name))
                    .map((k) => (
                      <TableRow key={k.key_name}>
                        <TableCell className="font-mono text-xs">{k.key_name}</TableCell>
                        <TableCell className="text-xs">
                          {k.rotated_at ? new Date(k.rotated_at).toLocaleString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MappingsPanel({ connectionId, tenantId }: { connectionId: string; tenantId: string }) {
  const [extCode, setExtCode] = useState("");
  const [carrierId, setCarrierId] = useState("");
  const [svc, setSvc] = useState("");
  const upsert = useServerFn(upsertServiceMapping);
  const del = useServerFn(deleteServiceMapping);
  const list = useServerFn(listMappings);
  const { data, refetch } = useQuery({
    queryKey: ["integration_mappings", connectionId, tenantId],
    queryFn: () => list({ data: { connectionId, tenantId } }),
  });
  const rows = data?.mappings ?? [];
  const carriers = data?.carriers ?? [];
  const load = () => refetch();
  return (
    <Card><CardContent className="p-4 space-y-3 text-sm">
      <div className="overflow-x-auto"><Table>
        <TableHeader><TableRow><TableHead>External code</TableHead><TableHead>Carrier</TableHead><TableHead>Service type</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.external_code}>
              <TableCell className="font-mono text-xs">{r.external_code}</TableCell>
              <TableCell>{carriers.find((c) => c.id === r.internal_carrier_id)?.name ?? "—"}</TableCell>
              <TableCell>{r.internal_service_level ?? "—"}</TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={async () => { await del({ data: { connectionId, externalCode: r.external_code } }); load(); }}>Remove</Button></TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-muted-foreground">No mappings.</TableCell></TableRow>}
        </TableBody>
      </Table></div>
      <div className="flex gap-2 items-end pt-2 border-t">
        <div className="flex-1"><Label>External code</Label><Input value={extCode} onChange={(e) => setExtCode(e.target.value)} placeholder="e.g. UPSGround" /></div>
        <div className="flex-1"><Label>Carrier</Label>
          <select className="w-full h-9 border rounded px-2 bg-background" value={carrierId} onChange={(e) => setCarrierId(e.target.value)}>
            <option value="">—</option>
            {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="flex-1"><Label>Service type</Label><Input value={svc} onChange={(e) => setSvc(e.target.value)} placeholder="ground" /></div>
        <Button size="sm" onClick={async () => {
          if (!extCode.trim()) return;
          try { await upsert({ data: { connectionId, externalCode: extCode.trim(), internalCarrierId: carrierId || null, internalServiceLevel: svc.trim() || null } }); setExtCode(""); setSvc(""); setCarrierId(""); load(); } catch (e) { toast.error((e as Error).message); }
        }}>Add</Button>
      </div>
    </CardContent></Card>
  );
}

function WebhooksPanel({ connectionId, provider }: { connectionId: string; provider: string }) {
  const [created, setCreated] = useState<{ secret: string; url: string } | null>(null);
  const create = useServerFn(createWebhook);
  const del = useServerFn(deleteWebhook);
  const list = useServerFn(listWebhooks);
  const { data, refetch } = useQuery({
    queryKey: ["integration_webhooks", connectionId],
    queryFn: () => list({ data: { connectionId } }),
  });
  const hooks = data?.webhooks ?? [];
  const load = () => refetch();
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  return (
    <Card><CardContent className="p-4 space-y-3 text-sm">
      <div className="flex justify-end">
        <Button size="sm" onClick={async () => {
          try {
            const r = await create({ data: { connectionId } });
            setCreated({ secret: r.secret, url: `${origin}/api/public/hooks/integrations/${provider}/${r.path_token}` });
            load();
          } catch (e) { toast.error((e as Error).message); }
        }}>Generate webhook</Button>
      </div>
      {created && (
        <Card className="border-primary/30">
          <CardHeader className="pb-2"><CardTitle className="text-sm">New Webhook (secret Shown Only Once)</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-xs font-mono break-all">
            <div>URL: {created.url}</div>
            <div>Secret: {created.secret}</div>
          </CardContent>
        </Card>
      )}
      <div className="overflow-x-auto"><Table>
        <TableHeader><TableRow><TableHead>URL</TableHead><TableHead>Last event</TableHead><TableHead>Total</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {hooks.map((h) => (
            <TableRow key={h.id}>
              <TableCell className="font-mono text-xs break-all">{origin}/api/public/hooks/integrations/{provider}/{h.path_token}</TableCell>
              <TableCell>{h.last_received_at ? new Date(h.last_received_at).toLocaleString() : "—"}</TableCell>
              <TableCell>{h.total_received}</TableCell>
              <TableCell><Button size="sm" variant="ghost" onClick={async () => { await del({ data: { webhookId: h.id } }); load(); }}>Delete</Button></TableCell>
            </TableRow>
          ))}
          {hooks.length === 0 && <TableRow><TableCell colSpan={4} className="text-muted-foreground">No webhooks.</TableCell></TableRow>}
        </TableBody>
      </Table></div>
    </CardContent></Card>
  );
}

function JobsPanel({ connectionId }: { connectionId: string }) {
  const retry = useServerFn(retryJob);
  const cancel = useServerFn(cancelJob);
  const list = useServerFn(listJobs);
  const { data, refetch } = useQuery({
    queryKey: ["integration_jobs", connectionId],
    queryFn: () => list({ data: { connectionId } }),
    refetchInterval: 5000,
  });
  const jobs = data?.jobs ?? [];
  const load = () => refetch();
  return (
    <Card><CardContent className="p-4 text-sm">
      <div className="overflow-x-auto"><Table>
        <TableHeader><TableRow><TableHead>Kind</TableHead><TableHead>Status</TableHead><TableHead>Attempts</TableHead><TableHead>Run at</TableHead><TableHead>Error</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {jobs.map((j) => (
            <TableRow key={j.id}>
              <TableCell>{j.kind}</TableCell>
              <TableCell><Badge variant={j.status === "succeeded" ? "default" : j.status === "dead" || j.status === "failed" ? "destructive" : "secondary"}>{j.status}</Badge></TableCell>
              <TableCell>{j.attempts}</TableCell>
              <TableCell className="text-xs">{new Date(j.run_at).toLocaleString()}</TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{j.error ?? "—"}</TableCell>
              <TableCell className="space-x-1">
                {(j.status === "failed" || j.status === "dead") && <Button size="sm" variant="outline" onClick={async () => { await retry({ data: { jobId: j.id } }); load(); }}>Retry</Button>}
                {(j.status === "queued" || j.status === "running") && <Button size="sm" variant="ghost" onClick={async () => { await cancel({ data: { jobId: j.id } }); load(); }}>Cancel</Button>}
              </TableCell>
            </TableRow>
          ))}
          {jobs.length === 0 && <TableRow><TableCell colSpan={6} className="text-muted-foreground">No jobs yet.</TableCell></TableRow>}
        </TableBody>
      </Table></div>
    </CardContent></Card>
  );
}

function ActivityPanel({ connectionId }: { connectionId: string }) {
  const list = useServerFn(listIntegrationEvents);
  const { data } = useQuery({
    queryKey: ["integration_events", connectionId],
    queryFn: () => list({ data: { connectionId } }),
  });
  const events = data?.events ?? [];
  return (
    <Card><CardContent className="p-4 text-sm">
      <div className="overflow-x-auto"><Table>
        <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Dir</TableHead><TableHead>Kind</TableHead><TableHead>HTTP</TableHead><TableHead>Duration</TableHead><TableHead>Error</TableHead></TableRow></TableHeader>
        <TableBody>
          {events.map((e) => (
            <TableRow key={e.id}>
              <TableCell className="text-xs">{new Date(e.created_at).toLocaleString()}</TableCell>
              <TableCell><Badge variant="secondary">{e.direction}</Badge></TableCell>
              <TableCell className="text-xs">{e.kind}</TableCell>
              <TableCell>{e.http_status ?? "—"}</TableCell>
              <TableCell className="text-xs">{e.duration_ms ? `${e.duration_ms}ms` : "—"}</TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{e.error ?? "—"}</TableCell>
            </TableRow>
          ))}
          {events.length === 0 && <TableRow><TableCell colSpan={6} className="text-muted-foreground">No activity yet.</TableCell></TableRow>}
        </TableBody>
      </Table></div>
    </CardContent></Card>
  );
}

function CertificationPanel({
  connectionId,
  tenantId,
  provider,
  env,
}: {
  connectionId: string;
  tenantId: string;
  provider: string;
  env: string;
}) {
  const run = useServerFn(runCarrierCertification);
  const runScenarios = useServerFn(runCertificationScenarios);
  const list = useServerFn(listCertifications);
  const [busy, setBusy] = useState<"idle" | "starting" | "running">("idle");

  const { data, refetch } = useQuery({
    queryKey: ["carrier_certifications", connectionId],
    queryFn: () => list(),
    refetchInterval: 4000,
  });
  const cert = (data?.rows ?? []).find(
    (r: { connection_id: string | null; env: string }) =>
      r.connection_id === connectionId && r.env === "sandbox",
  ) as
    | {
        id: string;
        status: string;
        scenarios_total: number;
        scenarios_passed: number;
        scenarios: Array<{ id: string; status: string; error?: string }>;
        last_error: string | null;
        certified_at: string | null;
      }
    | undefined;

  const supported = ["ups", "fedex", "dhl", "stamps"].includes(provider);

  return (
    <Card>
      <CardContent className="p-4 space-y-3 text-sm">
        {!supported && (
          <p className="text-muted-foreground">
            Certification is only available for ups, fedex, dhl, and stamps.
          </p>
        )}
        {supported && (
          <>
            <p className="text-muted-foreground text-xs">
              Runs every carrier scenario (auth → rate → label → void → tracking →
              address validate) against the sandbox. Promoting this connection to
              production is blocked until every scenario passes.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={env !== "sandbox" || busy !== "idle"}
                onClick={async () => {
                  try {
                    setBusy("starting");
                    await run({
                      data: {
                        tenantId,
                        connectionId,
                        carrier: provider as "ups" | "fedex" | "dhl" | "stamps",
                        env: "sandbox",
                      },
                    });
                    await refetch();
                    const fresh = (await list()).rows.find(
                      (r: { connection_id: string | null; env: string }) =>
                        r.connection_id === connectionId && r.env === "sandbox",
                    ) as { id: string } | undefined;
                    if (!fresh) throw new Error("certification row not found");
                    setBusy("running");
                    const result = await runScenarios({
                      data: { certificationId: fresh.id },
                    });
                    toast[result.status === "passed" ? "success" : "error"](
                      `Certification ${result.status} (${result.passed}/${result.total})`,
                    );
                    await refetch();
                  } catch (e) {
                    toast.error((e as Error).message);
                  } finally {
                    setBusy("idle");
                  }
                }}
              >
                {busy === "starting"
                  ? "Preparing…"
                  : busy === "running"
                    ? "Running scenarios…"
                    : cert
                      ? "Re-run certification"
                      : "Run sandbox certification"}
              </Button>
              {env !== "sandbox" && (
                <span className="text-xs text-muted-foreground">
                  Switch connection to sandbox to certify.
                </span>
              )}
              {cert && (
                <Badge
                  variant={
                    cert.status === "passed"
                      ? "default"
                      : cert.status === "failed"
                        ? "destructive"
                        : "secondary"
                  }
                >
                  {cert.status} · {cert.scenarios_passed}/{cert.scenarios_total}
                </Badge>
              )}
              {cert?.certified_at && (
                <span className="text-xs text-muted-foreground">
                  certified {new Date(cert.certified_at).toLocaleString()}
                </span>
              )}
            </div>

            {cert && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Scenario</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cert.scenarios.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-mono text-xs">{s.id}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              s.status === "passed"
                                ? "default"
                                : s.status === "failed"
                                  ? "destructive"
                                  : "secondary"
                            }
                          >
                            {s.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xl break-all">
                          {s.error ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}